<template>
    <div class="invoice-page">
        <div class="invoice-card">
            <InvoicePreview
                v-if="loaded"
                class="invoice-preview"
                :invoice="transformedInvoice"
                :company-profile="publicInvoice.company"
                :app-sales-info="appSalesInfo"
            >
                <DsFilledButton
                    v-if="showPayNow"
                    class="pay-now large"
                    @click="payNow"
                >
                    {{ payLabel }}
                </DsFilledButton>
            </InvoicePreview>

            <div v-else class="spinner-container">
                <DsSpinner />
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

import { orderStatus, invoiceTotalDue, InvoicePreview } from '@keap-web/money-lib';

const paymentSubType = {
    PAYPAL_COMMERCE_UCC: 'PAYPAL_COMMERCE_UCC',
};

export default {
    components: {
        InvoicePreview,
    },

    data() {
        return {
            loaded: false,
        };
    },

    created() {
        this.loadInvoice();
    },

    computed: {
        ...mapState({
            appSalesInfo: ({ sales }) => sales.appSalesInfo,
            paymentSubtype: ({ sales }) => sales.paymentSubtype,
            publicInvoice: ({ sales }) => sales.publicInvoice,
        }),

        invoice() {
            return this.transformedInvoice;
        },

        showPayNow() {
            return Boolean(invoiceTotalDue(this.invoice) > 0
                && this.paymentsReceivable
                && this.orderStatus !== orderStatus.PAID
                && this.orderStatus !== orderStatus.REFUNDED);
        },

        paymentsReceivable() {
            if (this.paymentSubtype === paymentSubType.PAYPAL_COMMERCE_UCC) {
                return (this.invoice.allowPayment && this.appSalesInfo.merchantSetUp && this.appSalesInfo.paypalEmailConfirmed)
                || (this.invoice.allowPaypal && this.appSalesInfo.paypalPaymentsReceivable);
            }

            return (this.invoice.allowPayment && this.appSalesInfo.merchantSetUp)
                || (this.invoice.allowPaypal && this.appSalesInfo.paypalPaymentsReceivable);
        },

        showPayDeposit() {
            return Boolean(this.showPayNow
                && this.invoice.payPlan
                && this.invoice.payPlan.amountDue
                && this.invoice.payPlan.amountDue > this.invoice.totalPaid);
        },

        payLabel() {
            return this.showPayDeposit ? this.$t('InvoicePage.header.payDeposit') : this.$t('InvoicePage.header.pay', { rate: this.$number(this.rate, 'currency') });
        },

        isDepositPaid() {
            return Boolean(this.publicInvoice
            && this.publicInvoice.payPlan
            && this.publicInvoice.payPlan.amountDue
            && this.publicInvoice.payPlan.amountDue <= this.totalPaid);
        },

        totalPaid() {
            let totalPaid = 0;

            if (this.publicInvoice && this.publicInvoice.paymentsMade) {
                this.publicInvoice.paymentsMade.forEach(({ amount }) => {
                    totalPaid += amount;
                });
            }

            return totalPaid;
        },

        transformedPayments() {
            return this.publicInvoice && this.publicInvoice.paymentsMade && this.publicInvoice.paymentsMade.map((payment) => {
                return {
                    ...payment,
                    payStatus: payment.paymentType,
                    payDate: payment.paymentItemDate,
                };
            });
        },

        transformedInvoice() {
            return {
                ...this.publicInvoice,
                totalDue: this.publicInvoice.totalDueNow || this.publicInvoice.totalRemainingBalance,
                dueBy: this.publicInvoice.dueDate,
                total: this.publicInvoice.totalAmount,
                status: this.orderStatus,
                refundTotal: this.publicInvoice.totalRefundAmount * -1,
                totalPaid: this.totalPaid,
                dateCreated: this.publicInvoice.createDate,
                paymentsMade: this.transformedPayments,
            };
        },

        orderStatus() {
            if (this.publicInvoice.totalRefundAmount > 0) {
                return orderStatus.REFUNDED;
            }

            if (this.publicInvoice.totalAmount <= this.totalPaid) {
                return orderStatus.PAID;
            }

            if (this.isDepositPaid) {
                return orderStatus.DEPOSIT_PAID;
            }

            return orderStatus.DRAFT;
        },

        rate() {
            return this.publicInvoice.totalDueNow || this.publicInvoice.totalRemainingBalance;
        },
    },

    methods: {
        setInvoice() {
            this.$store.commit('sales/SET_INVOICE', this.transformedInvoice);
        },

        payNow() {
            this.$router.push({ name: 'invoice.pay', params: this.$route.params, query: this.$route.query });
        },

        successfulLoad() {
            this.loaded = true;
            this.setInvoice();

            if (this.$route.query.print) {
                this.$nextTick(window.print());
            }
        },

        async loadInvoice() {
            const { xid } = this.$route.params;

            try {
                await this.$store.dispatch('sales/PUBLIC_LOAD_INVOICE', { xid });

                if (this.publicInvoice?.xid) {
                    this.successfulLoad();
                } else {
                    this.pageNotFound();
                }
            } catch (e) {
                this.pageNotFound();
            }
        },

        pageNotFound() {
            this.$router.replace({ name: 'page.not.found' });
        },
    },
};
</script>

<style lang="scss" scoped>
    .invoice-page {
        margin: $spacing-400 auto;
        padding-bottom: $spacing-200;
    }

    .invoice-preview {
        display: flex;
        flex-direction: column;
    }

    .invoice-card {
        @include card;
        max-width: px-to-rem(800px);
        margin: 0 auto;
        padding: 0;
    }

    .loading-container {
        padding: $spacing-600;
    }

    .pay-now {
        margin-bottom: $spacing-100;
    }

    @media only screen and ($small) {
        .invoice-page {
            margin: 0;
            padding: 0;
        }

        .invoice-card {
            border-radius: 0;
        }

        .invoice-preview {
            padding: 0;
            margin: 0;
            min-height: 100vh;
        }

        .pay-now {
            width: 100%;

            button {
                width: 100%;
            }
        }
    }

    @media print {
        .pay-now {
            display: none;
        }

        .container {
            border: 0;
            box-shadow: none;
        }

        .invoice-card {
            box-shadow: none;
            border: none;
        }
    }
</style>
